import React, {useEffect, useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {observer} from 'mobx-react';

import LoadingImg from '../components/LoadingImg';
import Dialog from '../components/ui-controls/Dialog';
import Text from '../components/ui-controls/Text';
import {useInterval} from '../hooks/useInterval';
import {AppRoute} from '../models/AppRoute';
import {RouteNavigatorScreenProps} from '../models/navigationTypes';
import {useStores} from '../stores/use-stores';
import theme from '../style/theme';

const TIME_BETWEEN_POLLS = 2000;
const MAX_POLL_TRIES = 15;

export type OrderProcessingScreenProps = RouteNavigatorScreenProps<AppRoute.CART_ORDER_PROCESSING>;

export default observer(({navigation}: OrderProcessingScreenProps) => {
  const {cartStore} = useStores();

  const [pollAttempt, setPollAttempt] = useState(0);
  const [timedOut, setTimedOut] = useState(false);

  const pollStatus = () => {
    cartStore.pollOrderStatus(navigation);
    setPollAttempt(pollAttempt + 1);
  };

  // Set up the polling interval.
  useInterval(
    async () => pollStatus(),
    TIME_BETWEEN_POLLS,
    !cartStore.orderProcessing ||
      cartStore.orderPayment3DSChallenged ||
      cartStore.orderPaymentIncomplete ||
      cartStore.orderFailedPayment ||
      pollAttempt >= MAX_POLL_TRIES,
  );

  // We have run out of poll attempts and the order still hasn't processed.
  useEffect(() => {
    if (pollAttempt >= MAX_POLL_TRIES && cartStore.orderProcessing) {
      setTimedOut(true);
    }
  }, [cartStore.orderProcessing, pollAttempt]);

  // Navigation action for OK button on either timeout or failed dialogue.
  const navigateToRecentOrders = () => {
    setTimedOut(false);
    // Reset the cartstore
    cartStore.fullyReloadOrderFromMFL();
    navigation.navigate(AppRoute.ACCOUNT_LOGGED_IN);
  };

  // Our waiting screen.
  const processing = () => (
    <>
      <LoadingImg />
      {!cartStore.orderFailedPayment && !cartStore.orderPaymentIncomplete && !timedOut ? (
        <Text style={styles.processingText}>{'.'.repeat(Math.ceil(pollAttempt % 5))}</Text>
      ) : null}
    </>
  );

  const timeoutDialog = () => (
    <Dialog
      isVisible={timedOut}
      title="Tired of waiting?"
      cancelVisible={false}
      okText="OK"
      onOk={navigateToRecentOrders}>
      <>
        <Text style={styles.dialogText}>
          It looks like your order is taking longer than usual to complete.
        </Text>
        <Text style={styles.dialogText}>We are still working on it.</Text>
        <Text style={styles.dialogText}>
          You can view the status of this, and other recent orders, from your account menu.
        </Text>
      </>
    </Dialog>
  );

  const failedPaymentDialog = () => (
    <Dialog
      isVisible={cartStore.orderFailedPayment || cartStore.orderPaymentIncomplete}
      title="Issue Taking Payment"
      cancelVisible={false}
      okText="OK"
      onOk={navigateToRecentOrders}>
      <>
        <Text style={styles.dialogText}>The payment for your order has failed.</Text>
        <Text style={styles.dialogText}>
          You can view the status of this, and other recent orders, from your account menu.
        </Text>
      </>
    </Dialog>
  );

  return (
    <View style={styles.primaryWaitingContainer} testID="order-processing-screen">
      {processing()}
      {timeoutDialog()}
      {failedPaymentDialog()}
    </View>
  );
});

const styles = StyleSheet.create({
  primaryWaitingContainer: {
    flex: 1,
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colour.background.general.primary,
  },
  container: {
    flex: 1,
  },
  dialogText: {
    marginVertical: 8,
    color: theme.colour.text.content.primary,
  },
  processingText: {
    color: theme.colour.text.drawer.primary,
  },
});
